import { HelmetProvider, Helmet } from "react-helmet-async";
import React, { useEffect, useMemo, useState, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import axios from "../../lib/axios";

interface MetaData {
    url: string;
    title: string;
    description?: string;
    keywords?: string;
}

interface MetaContextType {
    metaList: MetaData[];
}

const MetaContext = createContext<MetaContextType>({
    metaList: [],
});

const DEFAULT_META = {
    title: "Neo",
    description: "",
    keywords: "",
};

/**
 * Normalizes a URL/path to a consistent format for matching.
 * Handles both absolute URLs and relative paths.
 */
const normalizePath = (url: string) => {
    try {
        // Try parsing as a full URL or relative to current origin
        const parsed = new URL(url, window.location.origin);
        return parsed.pathname.replace(/\/+$/, "") || "/";
    } catch {
        // Fallback for simple string manipulation
        let path = url.replace(window.location.origin, "");
        return path.replace(/\/+$/, "") || "/";
    }
};

const findMetaForPath = (path: string, metaList: MetaData[]) => {
    const normalizedTarget = normalizePath(path).toLowerCase();
    return metaList.find((item) => {
        if (!item.url) return false;
        return normalizePath(item.url).toLowerCase() === normalizedTarget;
    });
};

/**
 * GlobalMetaUpdater reacts immediately to URL changes.
 * This ensures the title updates as soon as navigation starts, even if the page component 
 * is still loading or hasn't rendered its own PageMeta yet.
 */
export const GlobalMetaUpdater = () => {
    const location = useLocation();
    const { metaList } = useContext(MetaContext);

    const currentMeta = useMemo(() => {
        return findMetaForPath(location.pathname, metaList);
    }, [location.pathname, metaList]);

    if (!currentMeta) return null;

    return (
        <Helmet>
            <title>{currentMeta.title}</title>
            {currentMeta.description && <meta name="description" content={currentMeta.description} />}
            {currentMeta.keywords && <meta name="keywords" content={currentMeta.keywords} />}
            <meta property="og:title" content={currentMeta.title} />
            {currentMeta.description && <meta property="og:description" content={currentMeta.description} />}
        </Helmet>
    );
};

interface PageMetaProps {
    title?: string;
    description?: string;
    keywords?: string;
}

/**
 * PageMeta component used within individual pages.
 * It provides props for default/fallback titles and also checks the dynamic meta list.
 */
const PageMeta = ({ title, description, keywords }: PageMetaProps) => {
    const location = useLocation();
    const { metaList } = useContext(MetaContext);

    const dynamicMeta = useMemo(() => {
        return findMetaForPath(location.pathname, metaList);
    }, [location.pathname, metaList]);

    // Priority: Dynamic (DB) > Props > Default
    const finalTitle = dynamicMeta?.title || title || DEFAULT_META.title;
    const finalDescription = dynamicMeta?.description || description || DEFAULT_META.description;
    const finalKeywords = dynamicMeta?.keywords || keywords || DEFAULT_META.keywords;

    return (
        <Helmet>
            <title>{finalTitle}</title>
            <meta name="description" content={finalDescription} />
            <meta name="keywords" content={finalKeywords} />
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={finalDescription} />
        </Helmet>
    );
};

/**
 * AppWrapper should wrap the entire application.
 * It initializes the meta list and shares it via Context.
 */
export const AppWrapper = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const [metaList, setMetaList] = useState<MetaData[]>(() => {
        const cached = localStorage.getItem("meta-cache");
        try {
            return cached ? JSON.parse(cached) : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        // Fetch meta titles once on app load
        axios.get("/meta-title").then((res) => {
            if (Array.isArray(res.data)) {
                setMetaList(res.data);
                localStorage.setItem("meta-cache", JSON.stringify(res.data));
            }
        }).catch(err => {
            console.error("Failed to fetch meta titles:", err);
        });
    }, []);

    return (
        <HelmetProvider>
            <MetaContext.Provider value={{ metaList }}>
                {children}
            </MetaContext.Provider>
        </HelmetProvider>
    );
};

export default PageMeta;

//old code
// import { HelmetProvider, Helmet } from "react-helmet-async";

// const PageMeta = ({
//   title,
//   description,
// }: {
//   title: string;
//   description: string;
// }) => (
//   <Helmet>
//     <title>{title}</title>
//     <meta name="description" content={description} />
//   </Helmet>
// );

// export const AppWrapper = ({ children }: { children: React.ReactNode }) => (
//   <HelmetProvider>{children}</HelmetProvider>
// );